import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, throwError, concatMap, from } from 'rxjs';
import { CommonFunctionsService } from '../../services/common-functions.service';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { loginRequest, silentRedirectUri } from '../../auth-config';
import { CommonDataService } from '../../services/common-data.service';
import { CONSTANTS } from '../../Constants/Constants';
import { apiBaseURI } from '../../../environments/environment';

const normalizedApiBaseUri = apiBaseURI.replace(/\/$/, '').toLowerCase();

const shouldAttachApiHeaders = (requestUrl: string): boolean => {
  return requestUrl.toLowerCase().startsWith(normalizedApiBaseUri);
};

/**
 * Checks if the ID token is expired or about to expire within the given buffer (seconds).
 * Returns true if a force-refresh is needed.
 */
const isTokenExpiringSoon = (idToken: string, bufferSeconds = 300): boolean => {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    const expiryTime = payload.exp * 1000; // convert to ms
    const now = Date.now();
    return (expiryTime - now) < (bufferSeconds * 1000);
  } catch {
    return true; // if we can't decode, force refresh to be safe
  }
};

/**
 * Adds Authorization token (Bearer) to request headers
 */
const addToken = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
};

/**
 * Adds Estimate ID header to request, falls back to DEFAULT_ESTIMATE_ID if empty
 */
const addEstimateIdHeader = (request: HttpRequest<unknown>, commonDataService: CommonDataService): HttpRequest<unknown> => {
  const estimateId = commonDataService.getEstimateId();
  const headerValue = estimateId?.trim()
    ? estimateId
    : CONSTANTS.DEFAULT_ESTIMATE_ID;

  return request.clone({
    setHeaders: {
      'X-Estimate-Id': headerValue
    }
  });
};

/**
 * Proactively acquires a valid token via acquireTokenSilent (returns cached token
 * if not expired, otherwise refreshes silently). Falls back to interactive popup
 * if silent renewal fails, and ultimately redirects to login as a last resort.
 */
const acquireValidSiQuoteToken = (
  msalService: MsalService,
  commonFunctions: CommonFunctionsService
): Observable<AuthenticationResult> => {
  const account = msalService.instance.getActiveAccount();

  if (!account) {
    commonFunctions.login();
    return throwError(() => new Error('No active account found'));
  }

  return from(msalService.instance.acquireTokenSilent({
    scopes: loginRequest.scopes,
    account,
    redirectUri: silentRedirectUri
  })).pipe(
    catchError(error => {
      if (commonFunctions.isSilentRenewalTimeout(error)) {
        void commonFunctions.handleSessionExpiredDueToInactivity();
        return throwError(() => error);
      }

      if (error.name === 'InteractionRequiredAuthError') {
        console.log('Silent token acquisition failed, trying interactive popup');

        return from(msalService.instance.acquireTokenPopup(loginRequest)).pipe(
          catchError(interactiveError => {
            console.error('Interactive token acquisition failed:', interactiveError);
            commonFunctions.login();
            return throwError(() => new Error('Failed to acquire token interactively'));
          })
        );
      }

      return throwError(() => error);
    })
  );
};

/**
 * JWT Interceptor - Functional interceptor for Angular 20+
 * 
 * Responsibilities:
 * - Proactively acquires a valid token via acquireTokenSilent before each API request
 *   (returns cached token if valid, refreshes silently if expired)
 * - Adds Microsoft Entra ID (Azure AD) Bearer token to API requests
 * - Adds X-Estimate-Id header to API requests
 * - Falls back to interactive token acquisition if silent renewal fails
 * - Retries once on 401 as a safety net (e.g. server-side token revocation)
 */
export const jwtInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  if (!shouldAttachApiHeaders(req.url)) {
    return next(req);
  }

  const commonFunctions = inject(CommonFunctionsService);
  const msalService = inject(MsalService);
  const commonDataService = inject(CommonDataService);

  return acquireValidSiQuoteToken(msalService, commonFunctions).pipe(
    concatMap((result: AuthenticationResult) => {
      msalService.instance.setActiveAccount(result.account);

      // Proactive check: if the ID token is expired or expiring within 5 minutes,
      // force-refresh before sending the request (MSAL's cache logic is optimized
      // for access tokens and may return a stale ID token).
      if (isTokenExpiringSoon(result.idToken)) {
        console.log('ID token expiring soon, proactively force-refreshing');
        return from(msalService.instance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account: result.account,
          forceRefresh: true,
          redirectUri: silentRedirectUri
        })).pipe(
          concatMap((refreshedResult: AuthenticationResult) => {
            msalService.instance.setActiveAccount(refreshedResult.account);
            let request = addEstimateIdHeader(req, commonDataService);
            request = addToken(request, refreshedResult.idToken);
            return next(request);
          }),
          catchError((refreshError) => {
            if (commonFunctions.isSilentRenewalTimeout(refreshError)) {
              void commonFunctions.handleSessionExpiredDueToInactivity();
              return throwError(() => refreshError);
            }
            // If force-refresh fails, still try with the current token
            let request = addEstimateIdHeader(req, commonDataService);
            request = addToken(request, result.idToken);
            return next(request);
          })
        );
      }

      let request = addEstimateIdHeader(req, commonDataService);
      request = addToken(request, result.idToken);

      return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // Safety net: token was valid per MSAL cache but rejected by the server
            // (e.g. server-side revocation). Force-refresh once and retry.
            console.log('Got 401 despite valid token, force-refreshing');

            return from(msalService.instance.acquireTokenSilent({
              scopes: loginRequest.scopes,
              account: result.account,
              forceRefresh: true,
              redirectUri: silentRedirectUri
            })).pipe(
              concatMap((refreshedResult: AuthenticationResult) => {
                msalService.instance.setActiveAccount(refreshedResult.account);

                let retryRequest = addEstimateIdHeader(req, commonDataService);
                retryRequest = addToken(retryRequest, refreshedResult.idToken);

                return next(retryRequest);
              }),
              catchError((refreshError) => {
                if (commonFunctions.isSilentRenewalTimeout(refreshError)) {
                  void commonFunctions.handleSessionExpiredDueToInactivity();
                  return throwError(() => refreshError);
                }
                commonFunctions.login();
                return throwError(() => error);
              })
            );
          }
          return throwError(() => error);
        })
      );
    })
  );
};
